import React from 'react';
import { useTranslation } from 'react-i18next';
import { HeroIcons } from '@/components/design-system/atoms/icon';
import { LoadingSpinner2 } from '@/components/design-system/atoms/loading-spinner2';
import { theme } from '@/utils/util_theme';
import { useSearchRoomContext } from '@/features/marketplace/hooks/use-search-room';
import { SearchBarItem } from './components/search-bar-item';
import clsx from 'clsx';
import Routes from '../../../../routes';

const GotoSearchPageButton: React.FC<{ className?: string }> = ({ className }) => {
  const { t, i18n } = useTranslation();
  const { searchQuery } = useSearchRoomContext();

  const redirectToSearchPage = () => {
    const colocationsPath = Routes[`colocations_${i18n.language}_path`]

    window.location.href = colocationsPath({
      colocations: {
        query: searchQuery ? searchQuery : ''
      }
    })
  }

  return (
    <button
      className={clsx(
        'w-full rounded-[8px] border-0',
        'mt-3 px-[20px] py-[12px]',
        'flex items-center justify-center gap-2',
        'text-center text-lead2 font-bold text-text-primaryHeavy',
        'bg-fill-button-secondaryMedium',
        className
      )}
      onClick={redirectToSearchPage}
    >
      <span className="whitespace-nowrap">
        {t('js.features.marketplace.components.room_search.goto_search_page')}
      </span>

      <HeroIcons
        name="arrow-small-right"
        type="outline/medium"
        strokeColor={theme?.colors.fill.icon.primaryHeavy}
      />
    </button>
  )
}

export { GotoSearchPageButton };

const SearchBarItems: React.FC<{showGotoSearchPageButton?: boolean, style?: string}> = ({showGotoSearchPageButton = false, style = 'default'}) => {
  const {
    fetchSearchRoomResults: { isLoading, isFetching, data },
    searchQuery,
  } = useSearchRoomContext();

  const { t } = useTranslation();

  if (!searchQuery) {
    return <>
      <div className="text-lead2 font-bold text-text-primaryMedium text-start">
        {t('js.features.marketplace.components.room_search.start_typing_text')}
      </div>

      {showGotoSearchPageButton && <GotoSearchPageButton />}
    </>;
  }

  if (isLoading || isFetching) {
    return <LoadingSpinner2 />;
  }

  if (data?.data && data.data.length >= 1) {
    return (
      <>
        {data.data.map((item) => {
          return (
            <SearchBarItem
              key={item.path || item.name}
              item={item}
              style={style}
            />
          );
        })}

        {showGotoSearchPageButton && <GotoSearchPageButton className="mt-3" />}
      </>
    );
  }

  return (
    <div className="searchbar-no-results">
      {t('js.components.empty_state.no_results')}

      {showGotoSearchPageButton && <GotoSearchPageButton className="mt-3" />}
    </div>
  )
};

export { SearchBarItems };
